import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DirectDealFilterOptionValueSorterEnum } from '../../constants/filter/directDealFilter.constant';
import {
  DirectDealSliderFilterKeyType,
  directDealFilterSliderConstant,
} from '../../constants/filter/slider/directDealFilterSliderConstant';
import { filterSlice } from '../../store/filterSlice';
import { getDirectDealSliderIndex } from '../../utils/filter/directDealSliderValue.util';
import { useAppDispatch, useAppSelector } from '../store/useStore';

export const useUpdateDirectDealFilterStates = () => {
  const dispatch = useAppDispatch();
  const directDealFilterStates = useAppSelector((state) => state.filter.directDealFilter);
  const filterActions = filterSlice.actions;

  const [searchParams] = useSearchParams();

  const sortSearchParam = useMemo<string>(() => {
    if (searchParams.has('sort')) {
      return searchParams.get('sort')!;
    }
    return '';
  }, [searchParams]);

  const brandSearchParam = useMemo<string>(() => {
    if (searchParams.has('brand')) {
      return searchParams.get('brand')!;
    }
    return '';
  }, [searchParams]);

  const minEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('minEngineOutputPs')) {
      return searchParams.get('minEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const maxEngineOutputPsSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxEngineOutputPs')) {
      return searchParams.get('maxEngineOutputPs')!;
    }
    return '';
  }, [searchParams]);

  const minPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('minPrice')) {
      return searchParams.get('minPrice')!;
    }
    return '';
  }, [searchParams]);

  const maxPriceSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxPrice')) {
      return searchParams.get('maxPrice')!;
    }
    return '';
  }, [searchParams]);

  const statusSearchParam = useMemo<string>(() => {
    if (searchParams.has('status')) {
      return searchParams.get('status')!;
    }
    return '';
  }, [searchParams]);

  const machineTypeSearchParam = useMemo<string>(() => {
    if (searchParams.has('machineType')) {
      return searchParams.get('machineType')!;
    }
    return '';
  }, [searchParams]);

  const rowConfigSearchParam = useMemo<string>(() => {
    if (searchParams.has('rowConfig')) {
      return searchParams.get('rowConfig')!;
    }
    return '';
  }, [searchParams]);

  const minOperationTimeSearchParam = useMemo<string>(() => {
    if (searchParams.has('minOperationTime')) {
      return searchParams.get('minOperationTime')!;
    }
    return '';
  }, [searchParams]);

  const maxOperationTimeSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxOperationTime')) {
      return searchParams.get('maxOperationTime')!;
    }
    return '';
  }, [searchParams]);

  const minManufacturedYearSearchParam = useMemo<string>(() => {
    if (searchParams.has('minManufacturedYear')) {
      return searchParams.get('minManufacturedYear')!;
    }
    return '';
  }, [searchParams]);

  const maxManufacturedYearSearchParam = useMemo<string>(() => {
    if (searchParams.has('maxManufacturedYear')) {
      return searchParams.get('maxManufacturedYear')!;
    }
    return '';
  }, [searchParams]);

  const recentDaysSearchParam = useMemo<string>(() => {
    if (searchParams.has('recentDays')) {
      return searchParams.get('recentDays')!;
    }
    return '';
  }, [searchParams]);

  const regionSearchParam = useMemo<string>(() => {
    if (searchParams.has('region')) {
      return searchParams.get('region')!;
    }
    return '';
  }, [searchParams]);

  const sliderSearchParams = useMemo<
    { filterKey: DirectDealSliderFilterKeyType; minSearchParam: string; maxSearchParam: string }[]
  >(
    () => [
      {
        filterKey: 'engineOutputInPs',
        minSearchParam: minEngineOutputPsSearchParam,
        maxSearchParam: maxEngineOutputPsSearchParam,
      },
      { filterKey: 'price', minSearchParam: minPriceSearchParam, maxSearchParam: maxPriceSearchParam },
      {
        filterKey: 'manufacturedYear',
        minSearchParam: minManufacturedYearSearchParam,
        maxSearchParam: maxManufacturedYearSearchParam,
      },
      {
        filterKey: 'operationTime',
        minSearchParam: minOperationTimeSearchParam,
        maxSearchParam: maxOperationTimeSearchParam,
      },
    ],
    [
      minEngineOutputPsSearchParam,
      maxEngineOutputPsSearchParam,
      minPriceSearchParam,
      maxPriceSearchParam,
      minManufacturedYearSearchParam,
      maxManufacturedYearSearchParam,
      minOperationTimeSearchParam,
      maxOperationTimeSearchParam,
    ],
  );

  useEffect(() => {
    // search param 체크가 아직 안되었고, 필터 옵션에 brands가 모두 추가되었을 때 수행
    if (!directDealFilterStates.isSearchParamChecked && directDealFilterStates.isFilterOptionsAdded.brands) {
      dispatch(filterActions.resetAllFilters({ filterType: 'directDealFilter' }));

      // searchParams.size === 0가 있을 때만 필터 업데이트
      if (searchParams && searchParams.size > 0) {
        // 정렬 필터 업데이트
        if (sortSearchParam) {
          Object.values(DirectDealFilterOptionValueSorterEnum).forEach((sortOptionValues) => {
            if (sortSearchParam.includes(sortOptionValues as string)) {
              const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'sorter');
              const targetOption = targetFilter?.options.find((option) => option.value === sortOptionValues);

              if (targetFilter && targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'directDealFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // 브랜드 필터 업데이트
        if (brandSearchParam) {
          const brandIds = brandSearchParam.split(',');
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'filter_brand');

          // 한 옵션이 여러 브랜드 id를 가질 수 있어(TYM), id가 아닌 옵션 단위로 한 번씩만 선택한다.
          const targetOptions = targetFilter?.options.filter((option) =>
            String(option.value)
              .split(',')
              .some((brandId) => brandIds.includes(brandId)),
          );

          targetOptions?.forEach((targetOption) => {
            if (targetFilter) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 범위(슬라이더) 필터 업데이트. 한쪽만 있으면 나머지 한쪽은 '제한 없음'으로 둔다.
        sliderSearchParams.forEach(({ filterKey, minSearchParam, maxSearchParam }) => {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === filterKey);

          if (targetFilter && (minSearchParam || maxSearchParam)) {
            const sliderConstant = directDealFilterSliderConstant[filterKey];
            const minSliderIndex = minSearchParam
              ? getDirectDealSliderIndex(filterKey, Number(minSearchParam))
              : undefined;
            const maxSliderIndex = maxSearchParam
              ? getDirectDealSliderIndex(filterKey, Number(maxSearchParam))
              : undefined;

            dispatch(
              filterActions.changeFilterOptionValue({
                filterType: 'directDealFilter',
                targetFilter,
                optionValues: [minSliderIndex ?? sliderConstant.min, maxSliderIndex ?? sliderConstant.max],
              }),
            );
          }
        });

        // 판매 상태 필터 업데이트
        // 초기화 직후에는 기본값인 '판매 중'만 선택되어 있으므로, URL과 다른 옵션만 뒤집는다.
        if (searchParams.has('status')) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'status');
          // 'ALL'은 개편 전 URL에서 쓰던 값으로, 판매 상태 전체를 뜻한다.
          const statusValues = statusSearchParam === 'ALL' ? ['ONSALE', 'SOLDOUT'] : statusSearchParam.split(',');

          targetFilter?.options.forEach((targetOption) => {
            if (statusValues.includes(String(targetOption.value)) !== Boolean(targetOption.isDefault)) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 농기계 필터 업데이트
        if (machineTypeSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'machineType');

          machineTypeSearchParam.split(',').forEach((machineTypeOptionValue) => {
            const targetOption = targetFilter?.options.find((option) => option.value === machineTypeOptionValue);

            if (targetFilter && targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 조식 필터 업데이트
        if (rowConfigSearchParam) {
          rowConfigSearchParam.split(',').forEach((rowConfigOptionValue) => {
            const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'rowConfig');

            if (targetFilter) {
              const targetOption = targetFilter?.options.find((option) => option.value === rowConfigOptionValue);

              if (targetOption) {
                dispatch(
                  filterActions.changeFilterOptionIsSelected({
                    filterType: 'directDealFilter',
                    targetFilter,
                    targetOption,
                  }),
                );
              }
            }
          });
        }

        // 지역 필터 업데이트
        if (regionSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'region');

          regionSearchParam.split(',').forEach((regionOptionValue) => {
            const targetOption = targetFilter?.options.find((option) => option.value === regionOptionValue);

            if (targetFilter && targetOption) {
              dispatch(
                filterActions.changeFilterOptionIsSelected({
                  filterType: 'directDealFilter',
                  targetFilter,
                  targetOption,
                }),
              );
            }
          });
        }

        // 등록 기간 필터 업데이트
        if (recentDaysSearchParam) {
          const targetFilter = directDealFilterStates.globalFilters.find((filter) => filter.key === 'recentDays');
          const targetOption = targetFilter?.options.find((option) => String(option.value) === recentDaysSearchParam);

          if (targetFilter && targetOption) {
            dispatch(
              filterActions.changeFilterOptionIsSelected({
                filterType: 'directDealFilter',
                targetFilter,
                targetOption,
              }),
            );
          }
        }
      }

      // 마지막으로 isSearchParamChecked를 true로 설정
      dispatch(filterActions.changeIsSearchParamChecked({ filterType: 'directDealFilter', value: true }));
    }
  }, [
    dispatch,
    filterActions,
    directDealFilterStates.globalFilters,
    directDealFilterStates.isSearchParamChecked,
    directDealFilterStates.isFilterOptionsAdded.brands,
    searchParams,
    searchParams.size,
    brandSearchParam,
    sortSearchParam,
    sliderSearchParams,
    statusSearchParam,
    machineTypeSearchParam,
    rowConfigSearchParam,
    recentDaysSearchParam,
    regionSearchParam,
  ]);
};
